import { assertNotUndefined } from "@/util/typeguards.ts";
import {
    differenceInDays,
    differenceInMonths,
    differenceInYears,
    format,
    parse,
    parseISO,
} from "date-fns";
import { Resolution } from "@/types/generated/Resolution.ts";

export type DateRangeStrings = { from: string; to: string };

export const sortYYYYMMArray = (a: string, b: string) => {
    const yearAString = a.split("-")[0];
    const yearBString = b.split("-")[0];
    assertNotUndefined(yearAString);
    assertNotUndefined(yearBString);
    const yearA = parseInt(yearAString);
    const yearB = parseInt(yearBString);
    if (yearA < yearB) {
        return -1;
    }
    if (yearA > yearB) {
        return 1;
    }
    const monthAString = a.split("-")[1];
    const monthBString = b.split("-")[1];
    assertNotUndefined(monthAString);
    assertNotUndefined(monthBString);
    const monthA = parseInt(monthAString);
    const monthB = parseInt(monthBString);

    if (monthA < monthB) {
        return -1;
    }
    if (monthA > monthB) {
        return 1;
    }
    return 0;
};

export const dateToPretty = (value: string) =>
    format(parse(value, "yyyy-MM-dd", new Date()), "MMM d");

export const yearMonthToPretty = (value: string) =>
    format(
        parse(
            value.slice(0, 4) + "-" + value.slice(5, 7) + "-" + "01",
            "yyyy-MM-dd",
            new Date(),
        ),
        "MMM ''yy",
    );

export const getFormatterForResolution = (resolution: Resolution) => {
    switch (resolution) {
        case "Days":
            return dateToPretty;
        case "Months":
            return yearMonthToPretty;
        case "Years":
            return (value: string) => value;
        default:
            throw new Error("Unexpected resolution");
    }
};

// A tick's shape is its resolution ("2026", "2026-07", "2026-07-15"), as in
// data-api's get_resolution_from_date_tick. Reading it off the tick keeps a
// grid row from being formatted with the previous date range's resolution.
export const dateTickToPretty = (dateTick: string) => {
    const parts = dateTick.split("-").length;
    const resolution: Resolution =
        parts === 1 ? "Years" : parts === 2 ? "Months" : "Days";
    return getFormatterForResolution(resolution)(dateTick);
};

export const getDateRangeDescription = (
    dateRange: DateRangeStrings,
    resolution?: Resolution,
) => {
    switch (resolution) {
        case "Months":
            return (
                differenceInMonths(
                    parseISO(dateRange.to),
                    parseISO(dateRange.from),
                ) + " months"
            );
        case "Years":
            return (
                differenceInYears(
                    parseISO(dateRange.to),
                    parseISO(dateRange.from),
                ) + " years"
            );
        case "Days":
        default:
            return (
                differenceInDays(
                    parseISO(dateRange.to),
                    parseISO(dateRange.from),
                ) + " days"
            );
    }
};

export const dateTimeToTimezone = (
    dateTime: string | undefined,
    toTimezone: string,
) => {
    if (!dateTime) {
        return undefined;
    }

    const date = new Date(dateTime);

    // Method 1: Using toLocaleString
    const options: Intl.DateTimeFormatOptions = {
        timeZone: toTimezone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    };

    return date.toLocaleString(undefined, options);
};

/**
 * "1 October", for a limit that lifts or resets at midnight Utc. In Utc,
 * because read in a time zone west of Greenwich the same instant is the
 * evening before.
 */
export const utcDayToPretty = (value: string) =>
    new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "long",
        timeZone: "UTC",
    }).format(new Date(value));

/**
 * "Jul to Sep 2026", or "Nov 2026 to Jan 2027" across a new year: a range of
 * whole months, from its first and last day (Y-m-d).
 */
export const monthRangeToPretty = (dateFrom: string, dateTo: string) => {
    const month = (day: string, withYear: boolean) =>
        new Intl.DateTimeFormat("en", {
            month: "short",
            year: withYear ? "numeric" : undefined,
            timeZone: "UTC",
        }).format(new Date(`${day}T00:00:00Z`));
    const sameYear = dateFrom.slice(0, 4) === dateTo.slice(0, 4);

    return `${month(dateFrom, !sameYear)} to ${month(dateTo, true)}`;
};
